/* eslint-disable import/no-cycle */

import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { mergeClasses, debounce } from '@utils';

import Account from './components/Account';
import Cart from './components/Cart';
import Favourites from './components/Favourites';
import Menu from './components/Menu';

import Navigation from './modules/Navigation/Navigation';
import Search from './modules/Search/Search';
import TinyBanner from './modules/TinyBanner/TinyBanner';

export const HeaderContext = createContext();

export default function Header({
  navigationItems,
  tinyBanner,
  headerLogo,
  isLoggedIn,
  options: { locale }
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isHeaderVisible, toggleHeader] = useState(true);

  const [isMobile, setIsMobile] = useState(false);

  const [isNavigationWide, setIsNavigationWide] = useState(true);
  const [showLogo, setShowLogo] = useState(false);
  const navigationRef = useRef(null);

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      tinyBanner
    }),
    [isLoggedIn, tinyBanner]
  );

  useLayoutEffect(() => {
    const checkNavigationWidth = () => {
      if (navigationRef?.current) {
        const navigationWidth = navigationRef?.current?.offsetWidth || 0;
        const windowWidth = window?.innerWidth || 0;
        // 118 is the width of the Logo.
        setIsNavigationWide(navigationWidth > (windowWidth - 118) / 2);
      }
    };

    window.addEventListener('resize', debounce(checkNavigationWidth, 100));

    checkNavigationWidth();
    setShowLogo(true);

    return () => {
      window.removeEventListener('resize', checkNavigationWidth);
    };
  }, [navigationItems, navigationRef]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', debounce(updateSize, 100));
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      const currentScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      const offset = 25;

      if (currentScrollTop > lastScrollTop + offset && currentScrollTop > 500) {
        toggleHeader(false);
      } else if (
        currentScrollTop < lastScrollTop - offset ||
        currentScrollTop < 250
      ) {
        toggleHeader(true);
      }

      setLastScrollTop(currentScrollTop <= 0 ? 0 : currentScrollTop);
    });

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollTop]);

  return (
    <HeaderContext.Provider value={contextValue}>
      <div
        className={mergeClasses(
          'fixed inset-x-0 top-0 z-[5000] transition-transform duration-200 ease-in-out',
          {
            '-translate-y-full': !isHeaderVisible
          }
        )}
      >
        <div className="flex h-16 w-full items-center gap-x-2 border-0 border-b border-solid border-whisper bg-white">
          <div ref={navigationRef} className="flex w-fit shrink-0 items-center">
            <Navigation
              navigationItems={navigationItems?.top}
              tinyBanner={tinyBanner}
              isMobile={isMobile}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
            />
          </div>
          {showLogo && (
            <div
              className={mergeClasses(
                'relative mx-auto flex items-center justify-center',
                {
                  'lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:transform':
                    !isNavigationWide
                }
              )}
            >
              {headerLogo}
            </div>
          )}
          <div className="flex grow items-center justify-end">
            <Search
              options={{
                locale
              }}
            />
            <Account />
            <Favourites isVisible={isLoggedIn && !isMobile} />
            <Cart />
            <Menu setIsMenuOpen={setIsMenuOpen} />
          </div>
        </div>
        <TinyBanner />
      </div>
    </HeaderContext.Provider>
  );
}
